/**
 * useProactiveNudges — live nudges for any surface.
 *
 * Subscribes to the same store collections every other agent surface reads
 * (people, tasks, tickets, accounts), runs the PURE `scanSystem` sweep for
 * high findings, and feeds everything through the PURE `proactiveNudges`
 * engine. The hook owns ONLY the wiring; every judgement lives in the
 * tested engines, so the Copilot dock and the per-view "Argo suggests"
 * strips can never disagree about what the agent would suggest.
 *
 * Recomputes on every snapshot change and never writes anything itself —
 * accepting a nudge routes through `queueApproval` (the existing governed
 * proposal lane), which the rendering component owns.
 */

import { useEffect, useMemo, useState } from 'react';
import { subscribePeople } from '../db/people';
import { subscribeTasks } from '../db/tasks';
import { subscribeTickets } from '../db/tickets';
import { subscribeAccounts } from '../db/accounts';
import type { Account, Person, ProjectTask, Ticket } from '../db/types';
import { scanSystem } from './systemScanner';
import { proactiveNudges, type Nudge } from './proactiveNudges';

export function useProactiveNudges(): Nudge[] {
  const [people, setPeople] = useState<Person[]>([]);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);

  useEffect(() => {
    let active = true;
    const unsubs = [
      subscribePeople((r) => active && setPeople(r)),
      subscribeTasks((r) => active && setTasks(r)),
      subscribeTickets((r) => active && setTickets(r)),
      subscribeAccounts((r) => active && setAccounts(r)),
    ];
    return () => {
      active = false;
      unsubs.forEach((u) => u());
    };
  }, []);

  return useMemo(() => {
    const { findings } = scanSystem({ tickets, tasks, people, accounts });
    return proactiveNudges({ people, tasks, findings });
  }, [people, tasks, tickets, accounts]);
}
